import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  deleteForwardingAddress,
} from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import { ForwardingAddressModel } from '../../../../../../types';
import SharedDialog from '../../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import { Messages } from '../../../../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteForwardingAddressesDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedRows: string[];
  forwardingEmails: ForwardingAddressModel[];
  changeLogRoles: string[];
}

export default function DeleteForwardingAddressesDialog({
  open,
  handleClose,
  userEmail,
  selectedRows,
  forwardingEmails,
  changeLogRoles,
}: DeleteForwardingAddressesDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteForwardingAddress(selectedAddresses, userEmail, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted(
          'Forwarding address' + (selectedRows.length > 1 ? 'es' : '')
        ),
      });

      const newList = forwardingEmails.filter(
        (email) => !selectedRows.includes(email.forwardingEmail)
      );

      queryClient.setQueryData(
        [UserKeys.ForwardingAddresses, userEmail],
        newList
      );

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          'the selected forwarding address' +
            (selectedRows.length > 1 ? 'es' : '')
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .delete_forwarding_addresses,
        {
          module: ModuleList.modules.userManagement.name,
          section:
            ModuleList.modules.userManagement.sections.forwardingAddresses,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        UserRoles.DeleteGmailForwardAddress.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const selectedAddresses = forwardingEmails.filter((email) =>
    selectedRows.includes(email.forwardingEmail)
  );

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        selectedAddresses.length > 1
          ? 'Delete Forwarding Addresses'
          : 'Delete Forwarding Address'
      }
      dialogContent={
        <Box>
          <Typography sx={{ mb: 2 }}>
            {selectedAddresses.length > 1 ? (
              <>
                Delete{' '}
                <strong>{selectedAddresses.length} email addresses</strong> from{' '}
                <strong>{userEmail}'s</strong> forwarding addresses?
              </>
            ) : (
              <>
                Delete <strong>{selectedAddresses[0]?.forwardingEmail}</strong>{' '}
                from <strong>{userEmail}'s</strong> forwarding addresses?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title:
          selectedAddresses.length > 1 ? 'Delete Addresses' : 'Delete Address',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
